import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faUserPlus,
  faUser,
  faIdCard,
  faEnvelope,
  faPhone,
  faBriefcase,
  faUsers,
  faUserCheck,
  faUserSlash,
  faSpinner,
  faToggleOn,
  faToggleOff,
  faCheckCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import AddUserModal from "./user/AddUserModal";
import UserDetailView from "./user/UserDetailView";
import { statusStyles } from "../../utils/data";
import SearchBar from "../../components/ui/SearchBar";
import TableWrapper from "../../components/ui/TableWrapper";
import EmptyState from "../../components/ui/EmptyState";
import Pagination from "../../components/ui/Pagination";
import Modal from "../../components/ui/modal";
import { userService } from "../../services/service";
import { useAuth } from "../../hooks";
import { toastSuccess, toastError } from "../../utils/toast";

const ManageUser = () => {
  const { hasPermission } = useAuth();
  const canManageUser = hasPermission(["manage_user_account"]);

  const navigate = useNavigate();
  const { userId } = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [statusModal, setStatusModal] = useState({
    open: false,
    userId: null,
    userName: null,
    currentStatus: null,
    action: null,
  });
  const [permissions, setPermissions] = useState({});

  const queryClient = useQueryClient();
  const usersQuery = useQuery({
    queryKey: ["users", "all-with-roles"],
    queryFn: () => userService.getAllUsersWithRoles(),
    staleTime: Infinity,
  });
  const users = useMemo(() => {
    const response = usersQuery.data;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response)) return response;
    return Array.isArray(response?.users) ? response.users : [];
  }, [usersQuery.data]);
  const loading = usersQuery.isLoading || usersQuery.isFetching;
  const fetchUsers = () =>
    queryClient.invalidateQueries({ queryKey: ["users"] });

  const selectedUser = useMemo(() => {
    if (userId && users.length > 0) {
      return users.find((u) => u.userId === userId);
    }
    return null;
  }, [users, userId]);

  const filteredUsers = useMemo(() => {
    if (!Array.isArray(users) || users.length === 0) {
      return [];
    }

    let result = [...users];

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(
        (user) =>
          user.username?.toLowerCase().includes(search) ||
          user.full_name?.toLowerCase().includes(search) ||
          user.email?.toLowerCase().includes(search) ||
          user.phone?.includes(search) ||
          user.role?.toLowerCase().includes(search) ||
          user.status?.toLowerCase().includes(search),
      );
    }

    if (statusFilter === "active") {
      result = result.filter((user) => user.status?.toLowerCase() === "active");
    } else if (statusFilter === "inactive") {
      result = result.filter(
        (user) => user.status?.toLowerCase() === "inactive",
      );
    }

    if (result.length > 0) {
      result.sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
        return dateB - dateA;
      });
    }

    return result;
  }, [users, searchTerm, statusFilter]);

  const totalItems = filteredUsers.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentUsers = filteredUsers.slice(startIndex, startIndex + pageSize);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleAddUser = async (form) => {
    try {
      const response = await userService.create(form);
      if (response?.data) {
        toastSuccess("User added successfully!");
        fetchUsers();
        setAddUserOpen(false);
      }
    } catch (error) {
      console.error("Failed to add user:", error);
      toastError(
        error.response?.data?.message ||
          "Failed to add user. Please try again.",
      );
    }
  };

  const handleUpdateUser = async (updatedUser) => {
    try {
      const response = await userService.update(
        updatedUser.userId,
        updatedUser,
      );
      if (response?.data) {
        toastSuccess("User updated successfully!");
        fetchUsers();
      }
    } catch (error) {
      console.error("Failed to update user:", error);
      toastError(
        error.response?.data?.message ||
          "Failed to update user. Please try again.",
      );
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const action = currentStatus === "active" ? "deactivate" : "activate";
    try {
      if (action === "activate") {
        await userService.activateBoth(userId);
        toastSuccess("User and login activated successfully!");
      } else {
        await userService.deactivateBoth(userId);
        toastSuccess("User and login deactivated successfully!");
      }
      fetchUsers();
      setStatusModal({
        open: false,
        userId: null,
        userName: null,
        currentStatus: null,
        action: null,
      });
    } catch (error) {
      console.error("Failed to toggle user status:", error);
      toastError(
        error.response?.data?.message ||
          "Failed to update user status. Please try again.",
      );
    }
  };

  const handleViewUser = (user) => {
    navigate(`/app/users/${user.userId}`);
  };

  const handleBack = () => {
    navigate("/app/users");
  };

  const statusTabs = [
    { id: "all", label: "All", icon: faUsers, count: users.length },
    {
      id: "active",
      label: "Active",
      icon: faUserCheck,
      count: users.filter((u) => u.status?.toLowerCase() === "active").length,
    },
    {
      id: "inactive",
      label: "Inactive",
      icon: faUserSlash,
      count: users.filter((u) => u.status?.toLowerCase() === "inactive").length,
    },
  ];

  const formatRole = (role) => {
    const roleMap = {
      super_admin: "Super Admin",
      admin: "Admin",
      owner: "Shop Owner",
      seller: "Shop Seller",
    };
    return roleMap[role] || role || "N/A";
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: "bg-blue-900 text-white border-blue-900",
      admin: "bg-purple-500 text-white border-purple-500",
      owner: "bg-orange-600 text-white border-orange-600",
      seller: "bg-green-600 text-white border-green-600",
    };
    return colors[role] || "bg-gray-100 text-gray-700 border-gray-300";
  };

  const getStatusModalConfig = () => {
    const isActive = statusModal.currentStatus === "active";
    return {
      title: isActive ? "Deactivate User" : "Activate User",
      icon: isActive ? faExclamationTriangle : faCheckCircle,
      iconColor: isActive ? "text-yellow-500" : "text-green-500",
      bgColor: isActive ? "bg-yellow-50" : "bg-green-50",
      borderColor: isActive ? "border-yellow-200" : "border-green-200",
      buttonColor: isActive
        ? "bg-red-600 hover:bg-red-700"
        : "bg-green-600 hover:bg-green-700",
      buttonText: isActive ? "Deactivate" : "Activate",
      description: isActive
        ? "This user will not be able to login to the system until reactivated."
        : "This user will be able to login to the system again.",
    };
  };

  if (selectedUser) {
    return (
      <UserDetailView
        user={selectedUser}
        onBack={handleBack}
        permissions={permissions}
        setPermissions={setPermissions}
        onUpdateUser={handleUpdateUser}
      />
    );
  }

  if (userId && !selectedUser && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-gray-600">User not found</p>
        <Button
          onClick={handleBack}
          className="mt-4 bg-blue-800 hover:bg-blue-900 text-white"
        >
          Back to Users
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-12 w-12 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading users...</p>
        </div>
      </div>
    );
  }

  const modalConfig = getStatusModalConfig();

  return (
    <div className="flex flex-col gap-6">
      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faUsers}
                className="text-blue-800 h-5 w-5"
              />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
                Manage users
              </h2>
              <p className="text-sm text-gray-600 mt-0.5">
                Manage all users in the system ({users.length} total)
              </p>
            </div>
          </div>
          {canManageUser && (
            <Button
              onClick={() => setAddUserOpen(true)}
              className="gap-2 bg-blue-800 hover:bg-blue-900 text-white"
            >
              <FontAwesomeIcon icon={faUserPlus} className="h-4 w-4" />
              Add user
            </Button>
          )}
        </div>
      </Card>

      <Card className="bg-white border border-gray-200 px-4 py-3 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <SearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search by ID, name, email, phone, position..."
            className="flex-1"
          />
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {statusTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setStatusFilter(tab.id);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                  statusFilter === tab.id
                    ? "bg-blue-800 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
                }`}
              >
                <FontAwesomeIcon icon={tab.icon} className="h-3 w-3" />
                <span>{tab.label}</span>
                <span
                  className={`ml-0.5 px-1.5 py-0.5 text-xs rounded-full ${
                    statusFilter === tab.id
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </Card>

      <TableWrapper
        title="users"
        totalItems={totalItems}
        currentPage={currentPage}
        totalPages={totalPages}
        className="bg-white border border-gray-200 shadow-sm"
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                S/N
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                <FontAwesomeIcon icon={faIdCard} className="mr-1.5 h-3 w-3" />{" "}
                ID
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                <FontAwesomeIcon icon={faUser} className="mr-1.5 h-3 w-3" />{" "}
                Name
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                <FontAwesomeIcon icon={faEnvelope} className="mr-1.5 h-3 w-3" />{" "}
                Email
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                <FontAwesomeIcon icon={faPhone} className="mr-1.5 h-3 w-3" />{" "}
                Phone
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                <FontAwesomeIcon
                  icon={faBriefcase}
                  className="mr-1.5 h-3 w-3"
                />{" "}
                Position
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                Status
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 text-center">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentUsers.length > 0 ? (
              currentUsers.map((user, idx) => (
                <TableRow
                  key={user.userId || idx}
                  className="hover:bg-blue-50/30 transition-colors duration-150 border-b border-gray-100"
                >
                  <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                    {startIndex + idx + 1}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                    {user.username || "N/A"}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-sm text-gray-700">
                    {user.full_name || "N/A"}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-sm text-gray-600 break-all max-w-[150px]">
                    {user.email || "N/A"}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-sm text-gray-600 whitespace-nowrap">
                    {user.phone || "N/A"}
                  </TableCell>
                  <TableCell className="py-2.5 px-3">
                    <Badge
                      variant="outline"
                      className={getRoleBadgeColor(user.role)}
                    >
                      {formatRole(user.role)}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2.5 px-3">
                    <Badge
                      variant="outline"
                      className={
                        statusStyles[user.status?.toUpperCase()] ||
                        "bg-gray-100 text-gray-700 border-gray-300"
                      }
                    >
                      {user.status?.toUpperCase() || "N/A"}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2.5 px-3">
                    <div className="flex gap-1.5 justify-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewUser(user)}
                        className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50 h-8 px-2.5 text-xs"
                      >
                        <FontAwesomeIcon icon={faEye} className="h-3 w-3" />{" "}
                        View
                      </Button>
                      {canManageUser && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setStatusModal({
                              open: true,
                              userId: user.userId,
                              userName: user.full_name,
                              currentStatus: user.status,
                              action:
                                user.status === "active"
                                  ? "deactivate"
                                  : "activate",
                            })
                          }
                          className={`gap-1 h-8 px-2.5 text-xs ${
                            user.status === "active"
                              ? "border-yellow-500 text-yellow-600 hover:bg-yellow-50 hover:text-yellow-700"
                              : "border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              user.status === "active"
                                ? faToggleOff
                                : faToggleOn
                            }
                            className="h-3 w-3"
                          />
                          {user.status === "active" ? "Deactivate" : "Activate"}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <EmptyState
                    title="No users found"
                    message={
                      searchTerm
                        ? "Try adjusting your search or filters"
                        : statusFilter !== "all"
                          ? `No ${statusFilter} users found`
                          : "Add your first user to get started"
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      <AddUserModal
        isOpen={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        onSave={handleAddUser}
        onUserAdded={fetchUsers}
      />

      <Modal
        isOpen={statusModal.open}
        onClose={() =>
          setStatusModal({
            open: false,
            userId: null,
            userName: null,
            currentStatus: null,
            action: null,
          })
        }
        title={modalConfig.title}
        maxWidth="sm:max-w-md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() =>
                setStatusModal({
                  open: false,
                  userId: null,
                  userName: null,
                  currentStatus: null,
                  action: null,
                })
              }
              className="border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                handleToggleStatus(
                  statusModal.userId,
                  statusModal.currentStatus,
                )
              }
              className={`${modalConfig.buttonColor} text-white`}
            >
              {modalConfig.buttonText}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col items-center text-center py-4">
          <div
            className={`w-16 h-16 rounded-full ${modalConfig.bgColor} flex items-center justify-center mb-4 border ${modalConfig.borderColor}`}
          >
            <FontAwesomeIcon
              icon={modalConfig.icon}
              className={`h-8 w-8 ${modalConfig.iconColor}`}
            />
          </div>

          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            {modalConfig.buttonText} {statusModal.userName}?
          </h4>

          <p className="text-gray-600 text-sm max-w-sm">
            Are you sure you want to{" "}
            <strong>{modalConfig.buttonText.toLowerCase()}</strong> "
            {statusModal.userName}"?
          </p>

          <p
            className={`text-xs mt-3 px-4 py-2 rounded-lg ${modalConfig.bgColor} text-gray-700 max-w-sm`}
          >
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="mr-1.5 h-3 w-3 text-orange-600"
            />
            {modalConfig.description}
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default ManageUser;
